import { createReferralsPageView } from '@spotify-internal/event-definitions/es5/events/createReferralsPageView';
import { createReferralsPageViewNonAuth } from '@spotify-internal/event-definitions/es5/events/createReferralsPageViewNonAuth';
import {
  createReferralPageInteraction,
  ReferralPageInteractionEventData,
} from '@spotify-internal/event-definitions/es5/events/createReferralPageInteraction';
import { createReferralPageInteractionNonAuth } from '@spotify-internal/event-definitions/es5/events/createReferralPageInteractionNonAuth';
import { createWwwExperiments } from '@spotify-internal/event-definitions/es5/events/createWwwExperiments';
import { createWwwExperimentsNonAuth } from '@spotify-internal/event-definitions/es5/events/createWwwExperimentsNonAuth';
import { GabitoEventDefinition, EventDefinition, Tracker } from './tracking';
import { EventParams } from '../../server/types';
import { getCookie } from '../cookie-utils';
import { getClientOS, getDeviceType } from '../os-utils';

export type GabitoEventDefinitionsWithAuth =
  GabitoEventDefinition['options'] & { authorize: boolean };

export type ReferralPageViewEventParams = {
  page: string;
  market: string;
  http_referer?: string;
  sender_user_id?: string;
  share_id?: string;
};

export type ReferralPageInteractionEventParams = {
  page: string;
  market: string;
  referralCode: string;
};

export const shareButtonClickEvent = (
  eventData: ReferralPageInteractionEventParams,
): EventDefinition[] => [
  {
    tracker: Tracker.DataLayer,
    eventData: {
      event: 'createReferralCTA',
    },
  },
  {
    tracker: Tracker.Gabito,
    eventData: createReferralPageInteraction({
      page: eventData.page,
      market: eventData.market,
      interaction: 'SHARE_CTA_CLICKED',
      properties: {
        referral_code: eventData.referralCode,
      },
    }),
    options: {
      authorize: true,
    },
  },
];

export const productClickEvent = (
  params: Omit<ReferralPageInteractionEventParams, 'page' | 'interaction'> & {
    eventParams?: EventParams;
    component?: string;
    position?: number | null;
    componentType?: string;
    link?: string | null;
  },
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => {
  const eventData = {
    page: 'referrals',
    market: params.market,
    interaction: 'PRODUCT_CTA_CLICKED',
    properties: {
      'campaign-id': params.eventParams?.campaignId ?? '',
      'offer-type-id': params.eventParams?.offerTypeId ?? '',
      component: params.component ?? '',
      position: Number.isInteger(params.position)
        ? params.position!.toString()
        : '',
      href: params.link ?? '',
      referral_code: params.referralCode,
    },
  } as ReferralPageInteractionEventData;
  return [
    {
      tracker: Tracker.DataLayer,
      eventData: {
        event: 'productClick',
        ecommerce: {
          click: {
            products: [
              {
                country_code: eventData.market,
                variant: eventData.properties?.['campaign-id'],
                name: eventData.properties?.['offer-type-id'],
                list: eventData.properties?.component,
                position: eventData.properties?.position,
                href: eventData.properties?.href,
              },
            ],
          },
        },
      },
    },
    {
      tracker: Tracker.Gabito,
      eventData: options.authorize
        ? createReferralPageInteraction(eventData)
        : createReferralPageInteractionNonAuth(eventData),
      options,
    },
  ];
};

export const referralPageViewEvent = (
  eventData: ReferralPageViewEventParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createReferralsPageView({
          page: eventData.page,
          market: eventData.market,
          http_referer: eventData.http_referer,
          sender_user_id: eventData.sender_user_id,
          share_id: eventData.share_id,
        })
      : createReferralsPageViewNonAuth({
          page: eventData.page,
          market: eventData.market,
          sender_user_id: eventData.sender_user_id,
          share_id: eventData.share_id,
        }),
    options,
  },
];

export const errorPageViewEvent = (
  eventData: ReferralPageViewEventParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => [
  {
    tracker: Tracker.DataLayer,
    eventData: {
      event: 'referralError',
      eventLabel: eventData.page,
    },
  },
  {
    tracker: Tracker.Gabito,
    eventData: options.authorize
      ? createReferralsPageView({
          page: eventData.page,
          market: eventData.market,
          sender_user_id: eventData.sender_user_id,
          share_id: eventData.share_id,
        })
      : createReferralsPageViewNonAuth({
          page: eventData.page,
          market: eventData.market,
          sender_user_id: eventData.sender_user_id,
          share_id: eventData.share_id,
        }),
    options,
  },
];

type ExperimentEventFields = {
  action: string;
  label: string;
  variant: string;
  country: string;
  context: string;
};

const buildExperimentEvent = (
  fields: ExperimentEventFields,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] => {
  const eventFields = {
    category: 'referrals',
    action: fields.action,
    label: fields.label,
    variant: fields.variant,
    sp_t: getCookie('sp_t') ?? null,
    country: fields.country,
    os: getClientOS(),
    context: fields.context,
  };

  return [
    {
      tracker: Tracker.Gabito,
      eventData: options.authorize
        ? createWwwExperiments(eventFields)
        : createWwwExperimentsNonAuth(eventFields),
      options,
    },
  ];
};

export type ClickEventParams = {
  variant: string;
  country: string;
  context: string;
  label: string;
};

export const clickEvent = (
  eventData: ClickEventParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] =>
  buildExperimentEvent(
    {
      action: 'click',
      label: eventData.label,
      variant: eventData.variant,
      country: eventData.country,
      context: eventData.context,
    },
    options,
  );

export type ExposureEventParams = {
  variant: string;
  country: string;
  context: string;
};

export const exposureEvent = (
  eventData: ExposureEventParams,
  options: GabitoEventDefinitionsWithAuth,
): EventDefinition[] =>
  buildExperimentEvent(
    {
      action: 'exposure',
      label: getDeviceType(),
      variant: eventData.variant,
      country: eventData.country,
      context: eventData.context,
    },
    options,
  );
